/**
 * Walkthrough cache controls
 * @module
 */
import { createFileRoute } from "@tanstack/react-router";
import { formatBytes } from "gfx/MeshDataStats";
import THREEAssetLoader from "gfx/THREEAssetLoader";
import type { Walkthrough } from "gfx/walkthrough";
import {
  exportWalkthroughCache,
  importWalkthroughCache,
  openAndInitWalkthroughCacheDB,
  WALKTHROUGH_CACHE_INDEXEDDB_KEY,
  WALKTHROUGH_CACHE_MANIFESTS_STORE,
  WALKTHROUGH_CACHE_RESOURCES_STORE,
  type WalkthroughCacheProgress,
} from "gfx/walkthroughFetch";
import { walkthroughIterateResources } from "lib/components/walkthrough/walkthroughIterateResources";
import { useCallback, useEffect, useRef, useState } from "react";
import DebugLogViewer, { useDebugLogViewer } from "ui/components/DebugLogViewer";
import withAuthentication from "../../components/withAuthentication";

const groupStyle = {
  border: "1px solid #ddd",
  padding: "10px",
  borderRadius: "4px",
};

function listWalkthroughCacheInDB(): Promise<string[]> {
  return new Promise<string[]>((resolve) => {
    const openReq = openAndInitWalkthroughCacheDB();
    openReq.onerror = () => {};
    openReq.onblocked = () => {};
    openReq.onsuccess = () => {
      const db = openReq.result;

      const transaction = db.transaction([WALKTHROUGH_CACHE_MANIFESTS_STORE], "readwrite");

      const manifestStore = transaction.objectStore(WALKTHROUGH_CACHE_MANIFESTS_STORE);
      const listRequest = manifestStore.getAllKeys();
      listRequest.onsuccess = () => {
        resolve(listRequest.result as string[]);
      };

      transaction.commit();

      db.close();
    };
  });
}

function removeAlreadyCachedResourceURLs(walkthroughResources: string[], db: IDBDatabase): Promise<string[]> {
  return new Promise<string[]>((resolve) => {
    const transaction = db.transaction(WALKTHROUGH_CACHE_RESOURCES_STORE, "readwrite");
    const resourcesStore = transaction.objectStore(WALKTHROUGH_CACHE_RESOURCES_STORE);
    const listRequest = resourcesStore.getAllKeys();
    listRequest.onsuccess = () => {
      const list = listRequest.result as string[];
      resolve(walkthroughResources.filter((res) => !list.includes(res)));
    };
    transaction.commit();
  });
}

interface LoadProgress {
  loadedManifest: boolean;
  totalResourceCount: number;
  loadedResourceCount: number;
}

function WalkthroughCaching() {
  const [loadProgress, setLoadProgress] = useState<LoadProgress | undefined>(undefined);
  const [manifestURL, setManifestURL] = useState<string>(
    "https://photoreal-test.s3.us-east-1.amazonaws.com/builders//walkthroughs//manifest.json",
  );

  const [storageSize, setStorageSize] = useState<number | undefined>();
  const [storageSizeQuota, setStorageSizeQuota] = useState<number | undefined>();
  const [abortOnError, setAbortOnError] = useState<boolean>(false);
  const [refreshCached, setRefreshCached] = useState<boolean>(false);

  const [cachedManifestsList, setCachedManifestsList] = useState<string[]>([]);

  const [exportProgress, setExportProgress] = useState<WalkthroughCacheProgress | undefined>();
  const [importProgress, setImportProgress] = useState<WalkthroughCacheProgress | undefined>();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { addLogMessage, addErrorMessage, logElementRef, logMessages } = useDebugLogViewer();

  useEffect(() => {
    void (async () => {
      const manifestList = await listWalkthroughCacheInDB();
      setCachedManifestsList(manifestList);

      await refreshStorageEstimate();
    })();
  }, []);

  async function refreshStorageEstimate() {
    if (navigator.storage && navigator.storage.estimate) {
      // API not always supported on Safari!
      const estimate = await navigator.storage.estimate();
      if (!estimate || !estimate.usage || !estimate.quota) {
        return;
      }
      setStorageSize(estimate.usage);
      setStorageSizeQuota(estimate.quota);
    }
  }

  async function loadWalkthroughManifest(url: string): Promise<Walkthrough | undefined> {
    try {
      const manifestUrlToAdd: string = url;
      const response = await fetch(manifestUrlToAdd);
      if (!response.ok) {
        addErrorMessage(`Error loading manifest from ${url}`);
        return;
      }

      return await response.json();
    } catch (e) {
      addErrorMessage(`Error loading manifest from ${manifestURL}`);
    }
    return undefined;
  }

  async function addWalkthroughToCache() {
    setLoadProgress({ loadedManifest: false, loadedResourceCount: 0, totalResourceCount: 0 });
    const manifestUrlToAdd = manifestURL; // Store to a local variable since this function is async
    const walkthrough = await loadWalkthroughManifest(manifestUrlToAdd);
    if (!walkthrough) {
      return;
    }

    const webpSupported = await THREEAssetLoader.setupLossyWebPSupported();
    const avifSupported = await THREEAssetLoader.setupAvifSupported();
    if (!webpSupported) {
      addLogMessage(
        "Note that caching WebP textures is disabled since this browser does not support them. Fallback textures will be used when available.",
      );
    }
    if (!avifSupported) {
      addLogMessage(
        "Note that caching AVIF textures is disabled since this browser does not support them. Fallback textures will be used when available.",
      );
    }

    let walkthroughResources = walkthroughIterateResources(walkthrough, webpSupported, avifSupported);
    let loadedResourceCount = 0;
    const totalResourceCount = walkthroughResources.length;
    setLoadProgress({ loadedManifest: true, loadedResourceCount, totalResourceCount });

    const openReq = openAndInitWalkthroughCacheDB();
    openReq.onerror = () => {
      addErrorMessage("Failed to open walkthrough cache database");
    };
    openReq.onblocked = () => {
      addErrorMessage("Blocked to open walkthrough cache database");
    };
    openReq.onsuccess = async () => {
      const db = openReq.result;

      let countErrors = 0;

      if (!refreshCached) {
        walkthroughResources = await removeAlreadyCachedResourceURLs(walkthroughResources, db);
        const totalResourceCount = walkthroughResources.length;
        setLoadProgress({ loadedManifest: true, loadedResourceCount, totalResourceCount });
      }

      for (const url of walkthroughResources) {
        try {
          const resourceResponse = await fetch(url);
          if (!resourceResponse.ok) {
            addErrorMessage(`Error loading resource from ${url}`);
            ++countErrors;
            if (countErrors > 10 || abortOnError) {
              break;
            }
            continue;
          }
          const resourceBlob = await resourceResponse.blob();
          const transaction = db.transaction(WALKTHROUGH_CACHE_RESOURCES_STORE, "readwrite");
          const resourceStore = transaction.objectStore(WALKTHROUGH_CACHE_RESOURCES_STORE);
          resourceStore.put(resourceBlob, url); // Allow overwriting existing resources in case refreshCached is enabled
          transaction.commit();
          ++loadedResourceCount;
          setLoadProgress({ loadedManifest: true, loadedResourceCount, totalResourceCount });
        } catch (e) {
          addErrorMessage(`Error loading resource from ${url} ${e}`);
          ++countErrors;
          if (countErrors > 10 || abortOnError) {
            break;
          }
          continue;
        }
      }

      if (!abortOnError || countErrors === 0) {
        const transaction = db.transaction(WALKTHROUGH_CACHE_MANIFESTS_STORE, "readwrite");
        const manifestStore = transaction.objectStore(WALKTHROUGH_CACHE_MANIFESTS_STORE);
        manifestStore.add(walkthrough, manifestUrlToAdd);
        transaction.commit();

        if (!cachedManifestsList.includes(manifestUrlToAdd)) {
          setCachedManifestsList([...cachedManifestsList, manifestUrlToAdd]);
        }
      }

      if (countErrors === 0) {
        addLogMessage("Successfully stored walkthrough: " + manifestUrlToAdd);
      } else if (abortOnError) {
        addLogMessage("Aborted storing walkthrough because of errors when loading resources: " + manifestUrlToAdd);
      } else {
        addLogMessage("Stored subset of walkthrough resources with errors: " + manifestUrlToAdd);
      }

      db.close();

      await refreshStorageEstimate();
    };
  }

  function clearWalkthroughCache() {
    if (
      window.confirm(
        "Are you sure that you want to clear the cache? This will remove all loaded images and manifests from the cache.",
      )
    ) {
      window.indexedDB.deleteDatabase(WALKTHROUGH_CACHE_INDEXEDDB_KEY);
      setCachedManifestsList([]);
      addLogMessage("Cleared walkthrough cache");
    }
  }

  const handleExportCache = useCallback(async () => {
    addLogMessage("Starting cache export...");
    setExportProgress({ phase: "reading", current: 0, total: 1 });

    const blob = await exportWalkthroughCache((progress) => {
      setExportProgress(progress);
    });

    setExportProgress(undefined);

    if (!blob) {
      addErrorMessage("Export failed or cache is empty");
      return;
    }

    // Trigger download
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `walkthrough-cache-${new Date().toISOString().slice(0, 10)}.zip`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    addLogMessage(`Successfully exported cache (${formatBytes(blob.size)})`);
  }, [addLogMessage, addErrorMessage]);

  const handleImportCache = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) {
        return;
      }

      addLogMessage(`Starting cache import from ${file.name}...`);
      setImportProgress({ phase: "extracting", current: 0, total: 1 });

      const success = await importWalkthroughCache(file, (progress) => {
        setImportProgress(progress);
      });

      setImportProgress(undefined);

      if (success) {
        addLogMessage("Successfully imported cache");
        // Refresh the manifest list
        const manifestList = await listWalkthroughCacheInDB();
        setCachedManifestsList(manifestList);
        await refreshStorageEstimate();
      } else {
        addErrorMessage("Failed to import cache");
      }

      // Reset the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    },
    [addLogMessage, addErrorMessage],
  );

  return (
    <div style={{ padding: "10px" }}>
      <h2>Cache status</h2>
      <p>
        Note that browsers may automatically delete locally cached content to free disk space! For best results, make
        sure the cache is up to date by using this page <b>less than a week</b> before the planned demo.
      </p>
      {storageSize && storageSizeQuota ? (
        <p>
          Used local storage: <b>{formatBytes(storageSize)}</b> out of <b>{formatBytes(storageSizeQuota)}</b> possible
          quota (actual limit depends on available disk space)
        </p>
      ) : (
        ""
      )}
      <p>Cached walkthroughs:</p>
      <ul>
        {cachedManifestsList.map((manifestUrl) => (
          <li key={manifestUrl}>{manifestUrl}</li>
        ))}
      </ul>
      <h2>Manage cache</h2>
      <div style={groupStyle}>
        <p>
          <input type="checkbox" checked={abortOnError} onChange={(e) => setAbortOnError(e.target.checked)} /> Abort on
          error
        </p>
        <p>
          <input type="checkbox" checked={refreshCached} onChange={(e) => setRefreshCached(e.target.checked)} /> Refresh
          data that is already cached
        </p>
        <p>
          Manifest URL:{" "}
          <input
            type="text"
            value={manifestURL}
            onChange={(e) => {
              setManifestURL(e.target.value);
            }}
            size={100}
          />
        </p>
        <p>
          <button onClick={addWalkthroughToCache}>Add walkthrough to cache</button>
        </p>
      </div>
      <p>
        <button onClick={clearWalkthroughCache}>Clear walkthrough cache</button>
      </p>
      <h2>Export / Import</h2>
      <div style={groupStyle}>
        <p>
          <button onClick={handleExportCache} disabled={!!exportProgress}>
            {exportProgress ? "Exporting..." : "Export cache to file"}
          </button>
          {exportProgress && (
            <span style={{ marginLeft: "10px" }}>
              {exportProgress.phase}: {exportProgress.current} / {exportProgress.total}
            </span>
          )}
        </p>
        <p>
          <label>
            Import cache from file:{" "}
            <input
              type="file"
              accept=".zip"
              onChange={handleImportCache}
              disabled={!!importProgress}
              ref={fileInputRef}
            />
          </label>
          {importProgress && (
            <span style={{ marginLeft: "10px" }}>
              {importProgress.phase}: {importProgress.current} / {importProgress.total}
            </span>
          )}
        </p>
      </div>
      <h2>Log</h2>
      <div>
        {loadProgress ? (
          <p>
            <b>Current load progress:</b> Loaded resources: {loadProgress.loadedResourceCount} out of{" "}
            {loadProgress.totalResourceCount}. Loaded manifest: {loadProgress.loadedManifest ? "true" : "false"}
          </p>
        ) : (
          ""
        )}
        <DebugLogViewer logElementRef={logElementRef} logMessages={logMessages} />
      </div>
    </div>
  );
}

export const Route = createFileRoute("/debug/walkthrough-caching")({
  component: withAuthentication(WalkthroughCaching, { superAdmin: true }),
});
